import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const SECTIONS = ['environmental', 'social', 'governance'];
const SECTION_ICONS = { environmental: '🌱', social: '🤝', governance: '⚖️' };
const SECTION_LABELS = { environmental: 'Environmental', social: 'Social', governance: 'Governance' };

export default function QuestionnairePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [project, setProject] = useState(null);
  const [answers, setAnswers] = useState({}); // { questionId: { optionIndex, score, section } }
  const [currentSection, setCurrentSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Load questionnaire structure + existing answers
  useEffect(() => {
    Promise.all([
      axios.get('/api/questionnaire'),
      axios.get(`/api/projects/${id}`),
    ]).then(([qRes, pRes]) => {
      setQuestionnaire(qRes.data);
      setProject(pRes.data);
      // Restore saved answers
      const saved = {};
      for (const a of (pRes.data.answers || [])) {
        saved[a.questionId] = { optionIndex: null, score: a.score, section: a.section, answer: a.answer };
      }
      setAnswers(saved);
    }).catch(() => toast.error('Errore nel caricamento del questionario'));
  }, [id]);

  const handleAnswer = (questionId, section, optionIndex, optionLabel, score) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { optionIndex, score, section, answer: optionLabel },
    }));
  };

  const getSectionQuestions = useCallback((sectionKey) => {
    if (!questionnaire) return [];
    return questionnaire.sections[sectionKey]?.questions || [];
  }, [questionnaire]);

  const isSectionComplete = useCallback((sectionKey) => {
    if (!questionnaire) return false;
    const questions = getSectionQuestions(sectionKey);
    return questions.every(q => answers[q.id] !== undefined);
  }, [questionnaire, answers, getSectionQuestions]);

  const buildAnswerPayload = () => {
    if (!questionnaire) return [];
    const payload = [];
    for (const sectionKey of SECTIONS) {
      const questions = getSectionQuestions(sectionKey);
      for (const q of questions) {
        if (answers[q.id]) {
          payload.push({
            questionId: q.id,
            section: sectionKey,
            answer: answers[q.id].answer || '',
            score: answers[q.id].score ?? 0,
          });
        }
      }
    }
    return payload;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await axios.post(`/api/projects/${id}/answers`, { answers: buildAnswerPayload() });
      toast.success('Bozza salvata');
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Check all answered
    for (const sectionKey of SECTIONS) {
      if (!isSectionComplete(sectionKey)) {
        toast.error(`Completa la sezione "${SECTION_LABELS[sectionKey]}" prima di inviare`);
        setCurrentSection(SECTIONS.indexOf(sectionKey));
        return;
      }
    }
    setSubmitting(true);
    try {
      // Save all answers first
      await axios.post(`/api/projects/${id}/answers`, { answers: buildAnswerPayload() });
      // Then submit
      const { data } = await axios.post(`/api/projects/${id}/submit`);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Errore nell\'invio');
    } finally {
      setSubmitting(false);
    }
  };

  if (!questionnaire || !project) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9B9A92' }}>Caricamento questionario...</div>;
  }

  // Submitted result view
  if (result) {
    const l = result.level;
    const score = result.scoreTotal;
    return (
      <>
        <div className="topbar"><span className="topbar-title">Questionario inviato</span></div>
        <div className="page-content">
          <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <h1 style={{ fontSize: 26, marginBottom: 10 }}>Questionario inviato con successo!</h1>
            <p style={{ color: '#5C5B55', marginBottom: 28 }}>Il certificatore ha ricevuto una notifica e procederà alla revisione. Riceverai un'email con l'esito.</p>
            <div className="card" style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>
                {l.level === 3 ? '🥇' : l.level === 2 ? '🥈' : '🥉'}
              </div>
              <h2 style={{ fontSize: 22, marginBottom: 4 }}>Livello stimato: {l.label}</h2>
              <div style={{ fontSize: 36, fontWeight: 600, color: '#1D9E75', marginBottom: 4 }}>
                {result.percentage}%
              </div>
              <div style={{ fontSize: 14, color: '#9B9A92', marginBottom: 6 }}>
                {score} punti su 125
              </div>
              <p style={{ color: '#5C5B55', fontSize: 14 }}>{l.description}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
              {[
                { k: 'environmental', label: '🌱 Environmental', score: result.project.scoreEnvironmental, max: 30 },
                { k: 'social', label: '🤝 Social', score: result.project.scoreSocial, max: 45 },
                { k: 'governance', label: '⚖️ Governance', score: result.project.scoreGovernance, max: 50 },
              ].map(s => (
                <div key={s.k} className="stat-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#9B9A92', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 500 }}>
                    {Math.round(s.score)}<span style={{ fontSize: 13, color: '#9B9A92' }}>/{s.max}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#1D9E75' }}>
                    {Math.round((s.score / s.max) * 100)}%
                  </div>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Torna ai progetti</button>
          </div>
        </div>
      </>
    );
  }

  const sectionKey = SECTIONS[currentSection];
  const section = questionnaire.sections[sectionKey];
  const questions = section?.questions || [];
  const totalAnswered = Object.keys(answers).length;
  const totalQuestions = SECTIONS.reduce((s, k) => s + (questionnaire.sections[k]?.questions.length || 0), 0);
  const progressPct = Math.round((totalAnswered / totalQuestions) * 100);

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">{project.name} — Questionario ESG</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#9B9A92' }}>{totalAnswered}/{totalQuestions} risposte</span>
          <button className="btn btn-outline btn-sm" onClick={saveDraft} disabled={saving}>
            {saving ? 'Salvataggio...' : '💾 Salva bozza'}
          </button>
        </div>
      </div>
      <div className="page-content">
        <div style={{ maxWidth: 760 }}>
          {/* Overall progress */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: '#5C5B55' }}>
              <span>Completamento questionario</span>
              <span style={{ fontWeight: 500 }}>{progressPct}%</span>
            </div>
            <div className="progress"><div className="progress-bar" style={{ width: `${progressPct}%` }} /></div>
          </div>

          {/* Section wizard */}
          <div className="wizard-steps" style={{ marginBottom: 28 }}>
            {SECTIONS.map((sk, i) => (
              <div key={sk} className={`wizard-step ${i < currentSection ? 'done' : i === currentSection ? 'active' : ''}`}
                onClick={() => setCurrentSection(i)} style={{ cursor: 'pointer' }}>
                <div className="wizard-dot">{i < currentSection ? '✓' : i + 1}</div>
                <div className="wizard-label">{SECTION_ICONS[sk]} {SECTION_LABELS[sk]}</div>
              </div>
            ))}
            <div className={`wizard-step ${currentSection === 3 ? 'active' : ''}`}>
              <div className="wizard-dot">4</div>
              <div className="wizard-label">Riepilogo</div>
            </div>
          </div>

          {/* Current section */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
              {SECTION_ICONS[sectionKey]} {SECTION_LABELS[sectionKey]}
            </div>
            <h2 style={{ fontSize: 18, marginBottom: 4 }}>{section.label}</h2>
            <p style={{ color: '#5C5B55', fontSize: 14 }}>{section.description}</p>
          </div>

          {questions.map((q, qi) => {
            const saved = answers[q.id];
            return (
              <div key={q.id} className="question-card">
                <div className="question-text">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ color: '#9B9A92', fontSize: 12, fontWeight: 500 }}>{q.id}</span>
                    {q.area && (
                      <span style={{ background: '#EAF3DE', color: '#3B6D11', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 500 }}>
                        {q.area}
                      </span>
                    )}
                  </div>
                  {q.text}
                </div>
                {q.hint && (
                  <div style={{ fontSize: 12, color: '#7A7970', background: '#F7F7F5', borderRadius: 6, padding: '6px 10px', marginTop: 6, marginBottom: 2 }}>
                    💡 {q.hint}
                  </div>
                )}
                <div className="option-list">
                  {q.options.map((opt, oi) => {
                    const isSelected = saved?.answer === opt.label;
                    return (
                      <label key={oi} className={`option-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleAnswer(q.id, sectionKey, oi, opt.label, opt.score)}>
                        <input type="radio" name={q.id} checked={isSelected} onChange={() => {}} />
                        <span className="option-label">{opt.label}</span>
                       {/* <span className="option-score">{opt.score}/5</span> */}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {currentSection > 0 && (
                <button className="btn btn-outline" onClick={() => setCurrentSection(s => s - 1)}>← Precedente</button>
              )}
              <button className="btn btn-ghost" onClick={saveDraft} disabled={saving}>
                {saving ? '...' : 'Salva bozza'}
              </button>
            </div>
            {currentSection < SECTIONS.length - 1 ? (
              <button className="btn btn-primary" onClick={() => setCurrentSection(s => s + 1)}
                disabled={!isSectionComplete(sectionKey)}>
                Prossima sezione →
              </button>
            ) : (
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}
                style={{ background: '#085041' }}>
                {submitting ? 'Invio in corso...' : '📤 Invia per certificazione'}
              </button>
            )}
          </div>
          {currentSection < SECTIONS.length - 1 && !isSectionComplete(sectionKey) && (
            <p style={{ fontSize: 12, color: '#9B9A92', marginTop: 8, textAlign: 'right' }}>
              Rispondi a tutte le domande per passare alla sezione successiva
            </p>
          )}
        </div>
      </div>
    </>
  );
}
